import React from 'react';

enum GridCols {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  TWELVE = 12,
}

enum GridGap {
  NONE = 'none',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

enum GridAlign {
  START = 'start',
  CENTER = 'center',
  END = 'end',
  STRETCH = 'stretch',
}

interface GridProps {
  children: React.ReactNode;
  cols?: GridCols | {sm?: GridCols; md?: GridCols; lg?: GridCols};
  gap?: GridGap;
  alignItems?: GridAlign;
  justifyItems?: GridAlign;
  className?: string;
  useFlex?: boolean;
  style?: React.CSSProperties;
}

const colClasses = {
  [GridCols.ONE]: 'grid-cols-1',
  [GridCols.TWO]: 'grid-cols-2',
  [GridCols.THREE]: 'grid-cols-3',
  [GridCols.FOUR]: 'grid-cols-4',
  [GridCols.FIVE]: 'grid-cols-5',
  [GridCols.SIX]: 'grid-cols-6',
  [GridCols.TWELVE]: 'grid-cols-12',
};

const responsiveColClasses = {
  sm: {
    [GridCols.ONE]: 'sm:grid-cols-1',
    [GridCols.TWO]: 'sm:grid-cols-2',
    [GridCols.THREE]: 'sm:grid-cols-3',
    [GridCols.FOUR]: 'sm:grid-cols-4',
    [GridCols.FIVE]: 'sm:grid-cols-5',
    [GridCols.SIX]: 'sm:grid-cols-6',
    [GridCols.TWELVE]: 'sm:grid-cols-12',
  },
  md: {
    [GridCols.ONE]: 'md:grid-cols-1',
    [GridCols.TWO]: 'md:grid-cols-2',
    [GridCols.THREE]: 'md:grid-cols-3',
    [GridCols.FOUR]: 'md:grid-cols-4',
    [GridCols.FIVE]: 'md:grid-cols-5',
    [GridCols.SIX]: 'md:grid-cols-6',
    [GridCols.TWELVE]: 'md:grid-cols-12',
  },
  lg: {
    [GridCols.ONE]: 'lg:grid-cols-1',
    [GridCols.TWO]: 'lg:grid-cols-2',
    [GridCols.THREE]: 'lg:grid-cols-3',
    [GridCols.FOUR]: 'lg:grid-cols-4',
    [GridCols.FIVE]: 'lg:grid-cols-5',
    [GridCols.SIX]: 'lg:grid-cols-6',
    [GridCols.TWELVE]: 'lg:grid-cols-12',
  },
};

const gapClasses = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
};

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClasses = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
  stretch: 'justify-items-stretch',
};

export {GridCols, GridGap, GridAlign};

export const Grid: React.FC<GridProps> = ({
  children,
  cols = GridCols.ONE,
  gap = GridGap.MD,
  alignItems = GridAlign.STRETCH,
  justifyItems = GridAlign.STRETCH,
  className = '',
  useFlex = false,
  style,
}) => {
  const getColClasses = () => {
    if (typeof cols === 'number') {
      return colClasses[cols];
    }

    const responsive = [];
    if (cols.sm) responsive.push(responsiveColClasses.sm[cols.sm]);
    if (cols.md) responsive.push(responsiveColClasses.md[cols.md]);
    if (cols.lg) responsive.push(responsiveColClasses.lg[cols.lg]);

    return ['grid-cols-1', ...responsive].join(' ');
  };

  const baseClasses = [
    useFlex ? 'flex flex-wrap' : 'grid',
    !useFlex && getColClasses(),
    gapClasses[gap],
    alignClasses[alignItems],
    !useFlex && justifyClasses[justifyItems],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={baseClasses} style={style}>
      {children}
    </div>
  );
};
